import React, { useMemo } from 'react';

const FONT = "'Plus Jakarta Sans', sans-serif";

// Color palette for dining option groups — cycles through these based on index
const INDEXED_COLORS = [
    { dot: '#2563eb', bg: '#eff5ff', text: '#2563eb' },
    { dot: '#2563eb', bg: '#eff6ff', text: '#2563eb' },
    { dot: '#7c3aed', bg: '#f5f3ff', text: '#7c3aed' },
    { dot: '#d97706', bg: '#fffbeb', text: '#d97706' },
    { dot: '#0891b2', bg: '#ecfeff', text: '#0891b2' },
    { dot: '#dc2626', bg: '#fef2f2', text: '#dc2626' },
];

const getColorForIndex = (index) => INDEXED_COLORS[index % INDEXED_COLORS.length];

const formatTimeAgo = (time, date, t) => {
    if (!time) return '';
    try {
        const orderDate = date || new Date().toISOString().split('T')[0];
        const orderDateTime = new Date(`${orderDate}T${time}`);
        const now = new Date();
        const diffMs = now - orderDateTime;
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return t('just_now') || 'just now';
        if (diffMin < 60) return `${diffMin} ${t('min_ago') || 'min ago'}`;
        const diffHours = Math.floor(diffMin / 60);
        return `${diffHours}${t('h_ago') || 'h ago'}`;
    } catch {
        return '';
    }
};

const OrdersPanel = ({
    isOpen,
    onClose,
    pendingOrders,
    printedOrders,
    diningOptions,
    diningOptionObjects,
    currency,
    onOrderClick,
    t,
    language,
    getEntityTranslation,
    translateDynamic,
    translationDetails,
}) => {
    // Group orders by dining option, sorted by time within each group (newest first)
    const groupedOrders = useMemo(() => {
        const groups = {};

        Object.entries(pendingOrders || {}).forEach(([key, order]) => {
            const separatorIndex = key.indexOf('-');
            const diningOption = separatorIndex > -1 ? key.substring(0, separatorIndex) : key;
            const tableOrOrderNum = separatorIndex > -1 ? key.substring(separatorIndex + 1) : '';

            if (!groups[diningOption]) {
                groups[diningOption] = [];
            }
            groups[diningOption].push({
                key,
                diningOption,
                tableOrOrderNum,
                order,
                isPrinted: printedOrders?.[key] || false,
            });
        });

        // Sort each group by time (newest first)
        Object.values(groups).forEach(group => {
            group.sort((a, b) => {
                const timeA = a.order.time || '';
                const timeB = b.order.time || '';
                // Descending — newest first
                return timeB.localeCompare(timeA);
            });
        });

        // Return groups in the same order as diningOptions
        const ordered = [];
        (diningOptions || []).forEach(opt => {
            if (groups[opt]) {
                ordered.push({ diningOption: opt, orders: groups[opt] });
            }
        });
        // Include any groups not in diningOptions (edge case)
        Object.keys(groups).forEach(opt => {
            if (!ordered.find(g => g.diningOption === opt)) {
                ordered.push({ diningOption: opt, orders: groups[opt] });
            }
        });

        return ordered;
    }, [pendingOrders, printedOrders, diningOptions]);

    const totalOrders = Object.keys(pendingOrders || {}).length;

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.3)', zIndex: 40,
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                    transition: 'opacity 0.25s ease',
                }}
            />

            {/* Panel */}
            <div style={{
                position: 'fixed', top: 40, left: 0, bottom: 0, width: 400,
                background: '#ffffff', zIndex: 50,
                boxShadow: '4px 0 24px rgba(0,0,0,0.08)',
                transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                display: 'flex', flexDirection: 'column',
                fontFamily: FONT,
            }}>
                {/* Header */}
                <div style={{
                    padding: '20px 20px 16px', borderBottom: '1px solid #e2e8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <h2 style={{ fontSize: 18, fontWeight: 800, color: '#212529', margin: 0 }}>
                            {t('pending_orders') || 'Pending Orders'}
                        </h2>
                        <span style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                            {totalOrders} {totalOrders === 1 ? (t('order') || 'order') : (t('orders') || 'orders')} {t('across') || 'across'} {groupedOrders.length} {groupedOrders.length === 1 ? (t('dining_option') || 'dining option') : (t('dining_options') || 'dining options')}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            width: 32, height: 32, borderRadius: 8, border: 'none',
                            background: '#f1f5f9', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 18, color: '#64748b', transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; }}
                    >
                        &times;
                    </button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
                    {totalOrders === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px' }}>
                                <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
                                <path d="M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <div style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>
                                {t('no_pending_orders') || 'No pending orders'}
                            </div>
                        </div>
                    ) : (
                        groupedOrders.map((group, groupIndex) => {
                            const color = getColorForIndex(groupIndex);
                            const diningLabel = diningOptionObjects?.[group.diningOption]
                                ? (getEntityTranslation?.(diningOptionObjects[group.diningOption], language, translationDetails) || translateDynamic?.(group.diningOption, t) || group.diningOption)
                                : (translateDynamic?.(group.diningOption, t) || group.diningOption);

                            return (
                                <div key={group.diningOption} style={{ marginBottom: 16 }}>
                                    {/* Group header */}
                                    <div style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '8px 12px', borderRadius: 8, background: '#f8fafc',
                                        marginBottom: 8,
                                    }}>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#212529', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color.dot }} />
                                            {diningLabel}
                                        </span>
                                        <span style={{
                                            fontSize: 12, color: '#64748b', fontWeight: 600,
                                            background: '#e2e8f0', padding: '2px 8px', borderRadius: 10,
                                        }}>
                                            {group.orders.length}
                                        </span>
                                    </div>

                                    {/* Order cards */}
                                    {group.orders.map(({ key, tableOrOrderNum, order, isPrinted }) => {
                                        const itemCount = (order.products || []).reduce((sum, p) => sum + (p.quantity || 1), 0);
                                        const total = parseFloat(order.total) || 0;
                                        const timeAgo = formatTimeAgo(order.time, order.date, t);

                                        return (
                                            <div
                                                key={key}
                                                onClick={() => onOrderClick(key, order)}
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: 12,
                                                    padding: '10px 12px', borderRadius: 8,
                                                    marginBottom: 2, cursor: 'pointer',
                                                    transition: 'background 0.15s', background: 'transparent',
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.background = '#eff5ff';
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.background = 'transparent';
                                                }}
                                            >
                                                {/* Table/Order number badge */}
                                                <div style={{
                                                    width: 48, height: 48, borderRadius: 10, background: color.bg,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: tableOrOrderNum.length > 3 ? 13 : 16,
                                                    fontWeight: 800, color: color.text, flexShrink: 0,
                                                }}>
                                                    {tableOrOrderNum}
                                                </div>

                                                {/* Info */}
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                                                        <span style={{ fontSize: 14, fontWeight: 700, color: '#212529' }}>
                                                            {tableOrOrderNum.startsWith('#')
                                                                ? `${t('order') || 'Order'} ${tableOrOrderNum}`
                                                                : `${t('table') || 'Table'} ${tableOrOrderNum}`
                                                            }
                                                        </span>
                                                        <span style={{ fontSize: 11, color: '#64748b', fontWeight: 500 }}>
                                                            {timeAgo}
                                                        </span>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                        <span style={{ fontSize: 12, color: '#64748b' }}>
                                                            {itemCount} {itemCount === 1 ? (t('item') || 'item') : (t('items') || 'items')}
                                                        </span>
                                                        <span style={{ fontSize: 13, fontWeight: 700, color: '#2563eb' }}>
                                                            {currency} {total.toFixed(2)}
                                                        </span>
                                                        {isPrinted && (
                                                            <span style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                                                fontSize: 10, color: '#f59e0b', fontWeight: 600,
                                                                background: '#fef9c3', padding: '2px 6px', borderRadius: 4,
                                                            }}>
                                                                {t('sent') || 'SENT'}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Arrow */}
                                                <span style={{ color: '#94a3b8', fontSize: 16, flexShrink: 0 }}>&rsaquo;</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </>
    );
};

export default OrdersPanel;
